import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as api from '../../utils/api';
import styles from './OrderPage.module.css';
import { useAuth } from '../../context/AuthContext';
import OrderKeypad from '../../components/OrderKeypad';
import { FiArrowLeft, FiInfo, FiShield, FiTrendingUp, FiPlus, FiMinus } from 'react-icons/fi';
import { ThemeContext } from '../../context/ThemeContext';
import { usePrice } from '../../context/PriceContext';

import { isMarketOpen } from '../../utils/marketTimeChecker';

const getLotSize = (symbol) => {
  const sym = symbol.toUpperCase();
  if (sym.startsWith('NIFTY')) return 65;
  if (sym.startsWith('SENSEX')) return 20;
  if (sym.startsWith('BANKNIFTY')) return 35;
  if (sym.startsWith('BANKEX')) return 30;
  return 1;
};

const getInferredMetadata = (symbolName) => {
  const sym = symbolName.toUpperCase();
  const isOption = /\bCE\b|\bPE\b/.test(sym);
  
  let type = 'Equity';
  let exchange = 'NSE';

  if (isOption) {
      type = 'F&O';
      if (sym.startsWith('SENSEX') || sym.startsWith('BANKEX')) {
          exchange = 'BSE';
      } else {
          exchange = 'NSE';
      }
  }

  return { type, exchange, lotSize: getLotSize(sym) };
};

const OrderPage = () => {
  const { symbolName, tradeType } = useParams();
  const location = useLocation();
  const { user: authUser, refreshUser } = useAuth();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const { price: livePrice, lastDayClose: closePrice } = usePrice(symbolName);
  
  const currentLivePrice = livePrice || location.state?.livePrice || null;
  const lastDayClose = closePrice || null;

  const [priceChange, setPriceChange] = useState(0);
  const [priceChangePercent, setPriceChangePercent] = useState(0);

  const [orderType, setOrderType] = useState('market');
  const [positionType, setPositionType] = useState('Intraday');
  const [entryPrice, setEntryPrice] = useState('');
  const [lots, setLots] = useState(1);
  
  // Protection states
  const [targetEnabled, setTargetEnabled] = useState(false);
  const [targetPrice, setTargetPrice] = useState('');
  const [slEnabled, setSlEnabled] = useState(false);
  const [stoplossPrice, setStoplossPrice] = useState('');

  const [selectedMargin, setSelectedMargin] = useState(5);
  const [activeInput, setActiveInput] = useState(null);
  const [inlineMessage, setInlineMessage] = useState(null);

  const pageRef = useRef(null);

  // Inferred metadata logic
  const scripData = useMemo(() => getInferredMetadata(symbolName), [symbolName]);

  const marketCategory = scripData.type;

  const marketStatus = useMemo(() => {
    return isMarketOpen('equity');
  }, []);

  useEffect(() => {
    if (positionType === 'Carryforward') {
        setSelectedMargin(1);
    } else {
        if (selectedMargin === 1) setSelectedMargin(5);
    }
  }, [positionType, selectedMargin]);

  // Rely solely on hardcoded logic as requested
  const lotSize = scripData.lotSize;
  const quantity = lots * lotSize;
  const priceToUse = orderType === 'market' ? currentLivePrice : parseFloat(entryPrice);

  const requiredMargin = useMemo(() => {
    if (!priceToUse || !quantity || !selectedMargin) return 0;
    return (priceToUse * quantity) / selectedMargin;
  }, [priceToUse, quantity, selectedMargin]);

  useEffect(() => {
    if (currentLivePrice && lastDayClose) {
        const diff = currentLivePrice - lastDayClose;
        setPriceChange(diff);
        setPriceChangePercent((diff / lastDayClose) * 100);
    }
  }, [currentLivePrice, lastDayClose]);

  const handlePlaceOrder = async () => {
    if (!authUser || !priceToUse) return;
    if (authUser.fund < requiredMargin) {
        setInlineMessage('Insufficient balance.');
        return;
    }

    const orderPayload = {
        tradeSymbol: symbolName,
        orderType,
        tradeType,
        entryPrice: priceToUse,
        quantity, // Total quantity (lots * lotSize)
        targetPrice: targetEnabled && targetPrice ? parseFloat(targetPrice) : null,
        stoplossPrice: slEnabled && stoplossPrice ? parseFloat(stoplossPrice) : null,
        margin: selectedMargin,
        exchange: scripData.exchange,
        lotSize: lotSize,
        positionType
    };

    try {
        await api.placeOrder(orderPayload);
        setInlineMessage('Order placed successfully!');
        const targetTab = orderType === 'market' ? 'positions' : 'orders';
        localStorage.setItem('positionsPageTab', targetTab);
        refreshUser(localStorage.getItem('token'));
        setTimeout(() => navigate('/positions'), 1500);
    } catch (e) {
        setInlineMessage(e.response?.data?.msg || 'Placement failed.');
    }
  };

  const handleKeypadInput = (val) => {
    const setterMap = { lots: setLots, price: setEntryPrice, target: setTargetPrice, stoploss: setStoplossPrice };
    
    if (activeInput === 'lots') {
        setLots(prev => {
            const s = String(prev === 0 ? '' : prev);
            const newVal = parseInt(s + val);
            return isNaN(newVal) ? 0 : newVal;
        });
        return;
    }

    if (setterMap[activeInput]) {
        setterMap[activeInput](prev => {
            const s = String(prev || '');
            if (val === '.' && s.includes('.')) return prev;
            return s + val;
        });
    }
  };

  const handleKeypadBackspace = () => {
    if (activeInput === 'lots') {
      setLots(prev => Math.floor(prev / 10));
    } else {
      const setters = { 
        price: setEntryPrice, 
        target: setTargetPrice, 
        stoploss: setStoplossPrice 
      };
      if (setters[activeInput]) {
        setters[activeInput](prev => String(prev || '').slice(0, -1));
      }
    }
  };

  const handleAdjust = (field, delta) => {
    if (field === 'lots') {
        setLots(prev => Math.max(1, prev + delta));
    } else if (field === 'price') {
        const currentVal = parseFloat(entryPrice) || currentLivePrice || 0;
        const newVal = Math.max(0, currentVal + delta * 0.05).toFixed(2);
        setEntryPrice(newVal);
    }
  };

  const isBuy = tradeType.toLowerCase() === 'buy';

  return (
    <div className={`${styles.orderPage} ${styles[theme]}`} ref={pageRef} onClick={() => setActiveInput(null)}>
      {/* Dynamic colored header */}
      <div className={`${styles.header} ${isBuy ? styles.buyHeader : styles.sellHeader}`}>
        <button className={styles.backBtn} onClick={(e) => { e.stopPropagation(); navigate(-1); }}><FiArrowLeft /></button>
        <div className={styles.symbolInfo}>
          <h1>{symbolName}</h1>
          <span className={styles.exchangeBadge}>{scripData.exchange}</span>
        </div>
        <div className={styles.priceContainer}>
          <div className={styles.currentPrice}>₹{currentLivePrice?.toFixed(2) || '...'}</div>
          <div className={`${styles.priceChange} ${priceChange >= 0 ? styles.positive : styles.negative}`}>
            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
          </div>
        </div>
      </div>

      <div className={`${styles.scrollContent} ${activeInput ? styles.scrollWithKeypad : styles.scrollNoKeypad}`}>
        {/* Market Alert */}
        {!marketStatus.isOpen && (
          <div className={styles.marketAlert}>
            <FiInfo /> <span>{marketStatus.message}</span>
          </div>
        )}

        {/* Success/Error Toasts */}
        {inlineMessage && (
          <div className={`${styles.statusToast} ${inlineMessage.includes('successfully') ? styles.toastSuccess : styles.toastError}`}>
            {inlineMessage}
          </div>
        )}

        {/* Product Selection */}
        <div className={styles.inlineSection}>
          <label>Product</label>
          <div className={styles.radioGroup}>
            <div 
              className={`${styles.radioOption} ${positionType === 'Intraday' ? styles.activeRadio : ''}`}
              onClick={(e) => { e.stopPropagation(); setPositionType('Intraday'); setActiveInput(null); }}
            >
              <div className={styles.radioCircle}></div>
              <span>Intraday</span>
            </div>
            <div 
              className={`${styles.radioOption} ${positionType === 'Carryforward' ? styles.activeRadio : ''}`}
              onClick={(e) => { e.stopPropagation(); setPositionType('Carryforward'); setActiveInput(null); }}
            >
              <div className={styles.radioCircle}></div>
              <span>Overnight</span>
            </div>
          </div>
        </div>

        {/* Leverage Grid - ONLY for Intraday */}
        {positionType === 'Intraday' && (
          <div className={styles.marginSection} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sectionTitle}>
              <FiTrendingUp /> <span>Margin Leverage</span>
            </div>
            <div className={styles.leverageGrid}>
              {[1, 2, 3, 4, 5].map(v => (
                <button 
                  key={v} 
                  className={`${styles.leverageBtn} ${selectedMargin === v ? styles.activeLev : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedMargin(v); setActiveInput(null); }}
                >
                  {v}X
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Grid */}
        <div className={styles.mainInputs}>
          {/* Quantity/Lots Input */}
          <div className={`${styles.inputGroup} ${activeInput === 'lots' ? styles.activeInput : ''}`} onClick={(e) => { e.stopPropagation(); setActiveInput('lots'); }}>
            <div className={styles.inputLabelRow}>
                <label>{marketCategory === 'Equity' ? 'Qty' : 'Lots'}</label>
            </div>
            <div className={styles.modernInputWrapper}>
                <button className={styles.adjustBtn} onClick={(e) => { e.stopPropagation(); handleAdjust('lots', -1); }}><FiMinus /></button>
                <div className={styles.inputContent}>
                    <span className={styles.inputValue}>{lots}</span>
                    {activeInput === 'lots' && <span className={styles.cursor}>|</span>}
                </div>
                <button className={styles.adjustBtn} onClick={(e) => { e.stopPropagation(); handleAdjust('lots', 1); }}><FiPlus /></button>
            </div>
            <div className={styles.bottomHintRow}>
                {marketCategory !== 'Equity' ? (
                    <>
                        <span className={styles.lotHint}>1 Lot = {lotSize}</span>
                        <span className={styles.totalQtyText}>Total Qty: <b>{quantity}</b></span>
                    </>
                ) : (
                    <span className={styles.totalQtyText}>Standard Equity Lot</span>
                )}
            </div>
          </div>

          {/* Price Input */}
          <div 
            className={`${styles.inputGroup} ${activeInput === 'price' ? styles.activeInput : ''}`} 
            onClick={(e) => {
              e.stopPropagation();
              if (orderType === 'market') {
                setOrderType('limit');
                if (!entryPrice && currentLivePrice) {
                  setEntryPrice(currentLivePrice.toFixed(2));
                }
              }
              setActiveInput('price');
            }}
          >
            <div className={styles.inputLabelRow}>
                <label>Price</label>
            </div>
            <div className={`${styles.modernInputWrapper} ${orderType === 'market' ? styles.disabledWrapper : ''}`}>
                <button 
                  className={styles.adjustBtn} 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (orderType !== 'market') handleAdjust('price', -1); 
                  }} 
                  disabled={orderType === 'market'}
                >
                  <FiMinus />
                </button>
                <div className={styles.inputContent}>
                    {orderType === 'market' ? (
                        <span className={styles.marketPriceDisplay}>{currentLivePrice?.toFixed(2) || '0.00'}</span>
                    ) : (
                        <>
                            <span className={styles.inputValue}>{entryPrice || '0.00'}</span>
                            {activeInput === 'price' && <span className={styles.cursor}>|</span>}
                        </>
                    )}
                </div>
                <button 
                  className={styles.adjustBtn} 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (orderType !== 'market') handleAdjust('price', 1); 
                  }} 
                  disabled={orderType === 'market'}
                >
                  <FiPlus />
                </button>
            </div>
            <div className={styles.marketExecutionRow}>
              <div className={styles.checkboxLabel} onClick={(e) => e.stopPropagation()}>
                <input 
                  type="checkbox" 
                  checked={orderType === 'market'} 
                  onChange={(e) => {
                    if (e.target.checked) {
                      setOrderType('market');
                      setActiveInput(null);
                    } else {
                      setOrderType('limit');
                      setActiveInput('price');
                      if (!entryPrice && currentLivePrice) {
                        setEntryPrice(currentLivePrice.toFixed(2));
                      }
                    }
                  }} 
                />
                <label className={styles.marketLabelText}>Market Execution</label>
              </div>
            </div>
          </div>
        </div>

        {/* Integrated Protection Section */}
        <div className={styles.protectionContainer}>
          <div className={styles.sectionHeader} style={{marginTop: 0}}>
              <FiShield /> <span>Protection Settings</span>
          </div>
          <div className={styles.protectionInputs}>
            {/* Target Input */}
            <div 
              className={`${styles.inputGroup} ${activeInput === 'target' ? styles.activeInput : ''} ${!targetEnabled ? styles.disabledGroup : ''}`} 
              onClick={(e) => { 
                e.stopPropagation(); 
                if (targetEnabled) setActiveInput('target'); 
              }}
            >
              <div className={styles.inputLabelRow}>
                  <div className={styles.checkboxLabel}>
                      <input 
                          type="checkbox" 
                          checked={targetEnabled} 
                          onChange={(e) => { 
                            setTargetEnabled(e.target.checked); 
                            if(e.target.checked) setActiveInput('target');
                            else if(activeInput === 'target') setActiveInput(null); 
                          }} 
                      />
                      <label>Target</label>
                  </div>
              </div>
              <div className={`${styles.protectionInputWrapper} ${!targetEnabled ? styles.disabledWrapper : ''}`}>
                  <div className={styles.inputContent}>
                      <span className={styles.inputValue}>{targetPrice || '0.00'}</span>
                      {activeInput === 'target' && targetEnabled && <span className={styles.cursor}>|</span>}
                  </div>
              </div>
            </div>

            {/* Stoploss Input */}
            <div 
              className={`${styles.inputGroup} ${activeInput === 'stoploss' ? styles.activeInput : ''} ${!slEnabled ? styles.disabledGroup : ''}`} 
              onClick={(e) => { 
                e.stopPropagation(); 
                if (slEnabled) setActiveInput('stoploss'); 
              }}
            >
              <div className={styles.inputLabelRow}>
                  <div className={styles.checkboxLabel}>
                      <input 
                          type="checkbox" 
                          checked={slEnabled} 
                          onChange={(e) => { 
                            setSlEnabled(e.target.checked); 
                            if(e.target.checked) setActiveInput('stoploss');
                            else if(activeInput === 'stoploss') setActiveInput(null); 
                          }} 
                      />
                      <label>Stoploss</label>
                  </div>
              </div>
              <div className={`${styles.protectionInputWrapper} ${!slEnabled ? styles.disabledWrapper : ''}`}>
                  <div className={styles.inputContent}>
                      <span className={styles.inputValue}>{stoplossPrice || '0.00'}</span>
                      {activeInput === 'stoploss' && slEnabled && <span className={styles.cursor}>|</span>}
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Broker-style Footer */}
      <div className={`${styles.footer} ${activeInput ? styles.footerWithKeypad : ''}`}>
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <span className={styles.sumLabel}>Margin Required</span>
            <span className={styles.sumValue}>₹{requiredMargin.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.sumLabel}>Available Balance</span>
            <span className={styles.sumValue}>₹{authUser?.fund?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
        </div>
        <button 
          className={`${styles.actionBtn} ${isBuy ? styles.buyBtn : styles.sellBtn}`}
          onClick={(e) => { e.stopPropagation(); handlePlaceOrder(); }}
          disabled={!marketStatus.isOpen || !priceToUse}
        >
          {isBuy ? `BUY` : `SELL`}
        </button>
      </div>

      {activeInput && (
        <div className={styles.keypadWrapper} onClick={(e) => e.stopPropagation()}>
          <OrderKeypad 
            onInput={handleKeypadInput} 
            onBackspace={handleKeypadBackspace} 
          />
        </div>
      )}
    </div>
  );
};

export default OrderPage;
